/**
 * Synthesized notification sounds via Web Audio API.
 * No external assets required — fully procedural.
 *
 * Each preset is a short sequence of sine/triangle tones with envelope
 * shaping so the result sounds pleasant (and not like a computer beep).
 *
 * Supported event types:
 *   - "new_order": short two-tone "ding-dong" (rising 5th)
 *   - "order_pr":  "ka-ching" — bright triad arpeggio + sustained chord
 *   - "cliente_direto_pago": coin-drop chime — celebratory triplet for consultants
 *   - default:     single soft tone
 */

let _ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!_ctx) _ctx = new Ctx();
  // Resume if the browser auto-suspended after the first user gesture.
  if (_ctx.state === "suspended") {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

function playTone(ctx, { freq, start, duration, type = "sine", gain = 0.18 }) {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
  // ADSR-ish envelope to avoid clicks
  const t0 = ctx.currentTime + start;
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(env);
  env.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

const PRESETS = {
  // Two-tone rising 5th — soft, notification-like
  new_order: (ctx) => {
    playTone(ctx, { freq: 587.33, start: 0.00, duration: 0.18, type: "sine", gain: 0.20 }); // D5
    playTone(ctx, { freq: 880.00, start: 0.18, duration: 0.32, type: "sine", gain: 0.20 }); // A5
  },
  // "Ka-ching" cash register — bright bell-like triad
  order_pr: (ctx) => {
    // Quick arpeggio E5–G#5–B5 (Major triad) then sustained shimmer
    playTone(ctx, { freq: 659.25, start: 0.00, duration: 0.22, type: "triangle", gain: 0.18 }); // E5
    playTone(ctx, { freq: 830.61, start: 0.07, duration: 0.22, type: "triangle", gain: 0.18 }); // G#5
    playTone(ctx, { freq: 987.77, start: 0.14, duration: 0.55, type: "triangle", gain: 0.20 }); // B5 sustain
    // High shimmer to give it the "metallic" cash-register feel
    playTone(ctx, { freq: 1318.51, start: 0.05, duration: 0.45, type: "sine", gain: 0.10 });    // E6
    playTone(ctx, { freq: 1975.53, start: 0.10, duration: 0.55, type: "sine", gain: 0.07 });    // B6
  },
  // Coin-drop chime for consultants — bright C-major arpeggio + sparkle
  cliente_direto_pago: (ctx) => {
    playTone(ctx, { freq: 523.25, start: 0.00, duration: 0.16, type: "triangle", gain: 0.18 }); // C5
    playTone(ctx, { freq: 659.25, start: 0.10, duration: 0.16, type: "triangle", gain: 0.18 }); // E5
    playTone(ctx, { freq: 783.99, start: 0.20, duration: 0.45, type: "triangle", gain: 0.20 }); // G5 sustain
    playTone(ctx, { freq: 1046.50, start: 0.10, duration: 0.55, type: "sine", gain: 0.10 });    // C6 shimmer
    playTone(ctx, { freq: 1568.00, start: 0.20, duration: 0.40, type: "sine", gain: 0.06 });    // G6 sparkle
  },
  // Soft fallback
  _default: (ctx) => {
    playTone(ctx, { freq: 660, start: 0, duration: 0.25, type: "sine", gain: 0.16 });
  },
};

export function playNotificationSound(eventType) {
  const ctx = getCtx();
  if (!ctx) return;
  const preset = PRESETS[eventType] || PRESETS._default;
  try {
    preset(ctx);
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("playNotificationSound failed", e);
  }
}
